import { useState, useRef } from 'react';
import { AboutSettings, ValueItem, StatItem } from '@/hooks/useAboutSettings';

interface AboutEditorProps {
  settings: AboutSettings;
  onSave: (s: AboutSettings) => void;
}

type EditorTab = 'hero' | 'story' | 'mission' | 'values' | 'stats' | 'cta';

const ICON_OPTIONS = [
  'ri-heart-line', 'ri-heart-fill', 'ri-star-line', 'ri-leaf-line',
  'ri-microscope-line', 'ri-community-line', 'ri-seedling-line',
  'ri-shield-check-line', 'ri-lightbulb-line', 'ri-hand-heart-line',
  'ri-award-line', 'ri-chat-smile-2-line', 'ri-eye-line', 'ri-book-open-line',
  'ri-home-heart-line', 'ri-trophy-line', 'ri-plant-line', 'ri-user-heart-line',
];

export default function AboutEditor({ settings, onSave }: AboutEditorProps) {
  const [form, setForm] = useState<AboutSettings>(JSON.parse(JSON.stringify(settings)));
  const [activeTab, setActiveTab] = useState<EditorTab>('hero');
  const [saved, setSaved] = useState(false);
  const heroFileRef = useRef<HTMLInputElement>(null);
  const storyFileRef = useRef<HTMLInputElement>(null);

  const updateHero = (field: keyof AboutSettings['hero'], value: string) => {
    setForm(p => ({ ...p, hero: { ...p.hero, [field]: value } }));
    setSaved(false);
  };
  const updateStory = (field: keyof Omit<AboutSettings['story'], 'paragraphs'>, value: string) => {
    setForm(p => ({ ...p, story: { ...p.story, [field]: value } }));
    setSaved(false);
  };
  const updateParagraph = (i: number, value: string) => {
    setForm(p => {
      const paragraphs = [...p.story.paragraphs];
      paragraphs[i] = value;
      return { ...p, story: { ...p.story, paragraphs } };
    });
    setSaved(false);
  };
  const addParagraph = () => {
    setForm(p => ({ ...p, story: { ...p.story, paragraphs: [...p.story.paragraphs, ''] } }));
  };
  const removeParagraph = (i: number) => {
    setForm(p => ({ ...p, story: { ...p.story, paragraphs: p.story.paragraphs.filter((_, idx) => idx !== i) } }));
  };
  const updateMission = (field: keyof AboutSettings['mission'], value: string) => {
    setForm(p => ({ ...p, mission: { ...p.mission, [field]: value } }));
    setSaved(false);
  };
  const updateValueItem = (i: number, field: keyof ValueItem, value: string) => {
    setForm(p => {
      const items = [...p.values.items];
      items[i] = { ...items[i], [field]: value };
      return { ...p, values: { ...p.values, items } };
    });
    setSaved(false);
  };
  const addValueItem = () => {
    setForm(p => ({
      ...p,
      values: { ...p.values, items: [...p.values.items, { icon: 'ri-star-line', title: '', description: '' }] },
    }));
  };
  const removeValueItem = (i: number) => {
    setForm(p => ({ ...p, values: { ...p.values, items: p.values.items.filter((_, idx) => idx !== i) } }));
  };
  const updateStatItem = (i: number, field: keyof StatItem, value: string) => {
    setForm(p => {
      const items = [...p.stats.items];
      items[i] = { ...items[i], [field]: value };
      return { ...p, stats: { items } };
    });
    setSaved(false);
  };
  const updateCta = (field: keyof AboutSettings['cta'], value: string) => {
    setForm(p => ({ ...p, cta: { ...p.cta, [field]: value } }));
    setSaved(false);
  };

  const handleImageFile = (field: 'hero' | 'story', e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = ev => {
      const val = ev.target?.result as string;
      if (field === 'hero') updateHero('image', val);
      else updateStory('image', val);
    };
    reader.readAsDataURL(file);
  };

  const handleSave = () => {
    onSave(form);
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  };

  const tabs: { id: EditorTab; label: string; icon: string }[] = [
    { id: 'hero', label: 'Hero', icon: 'ri-image-line' },
    { id: 'story', label: 'História', icon: 'ri-book-open-line' },
    { id: 'mission', label: 'Missão', icon: 'ri-focus-3-line' },
    { id: 'values', label: 'Valores', icon: 'ri-heart-line' },
    { id: 'stats', label: 'Números', icon: 'ri-bar-chart-line' },
    { id: 'cta', label: 'CTA', icon: 'ri-cursor-line' },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-heading font-bold text-foreground-950 text-xl md:text-2xl">
            Editar Página Sobre Nós
          </h2>
          <p className="text-foreground-500 text-sm mt-1">
            Personalize todas as seções da página sobre o blog.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setForm(JSON.parse(JSON.stringify(settings)))}
            className="px-4 py-2 text-sm text-foreground-600 border border-background-300 rounded-md hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
          >
            Descartar
          </button>
          <button
            onClick={handleSave}
            className={`px-4 py-2 text-sm font-bold rounded-md transition-colors cursor-pointer whitespace-nowrap flex items-center gap-2 ${
              saved ? 'bg-green-500 text-white' : 'bg-primary-500 hover:bg-primary-600 text-white'
            }`}
          >
            {saved ? (
              <><i className="ri-checkbox-circle-line"></i> Salvo!</>
            ) : (
              <><i className="ri-save-line"></i> Salvar alterações</>
            )}
          </button>
        </div>
      </div>

      {/* Section tabs */}
      <div className="flex flex-wrap gap-1 border-b border-background-200 pb-0">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-1.5 px-3 py-2 text-xs font-bold rounded-t-lg cursor-pointer transition-colors whitespace-nowrap border-b-2 -mb-px ${
              activeTab === tab.id
                ? 'border-primary-500 text-primary-600 bg-primary-50'
                : 'border-transparent text-foreground-500 hover:text-foreground-700 hover:bg-background-100'
            }`}
          >
            <i className={`${tab.icon} text-sm`}></i>
            {tab.label}
          </button>
        ))}
      </div>

      {/* ── HERO TAB ── */}
      {activeTab === 'hero' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="space-y-4">
            <div>
              <label className="block text-xs font-bold text-foreground-600 uppercase tracking-wide mb-1.5">Título</label>
              <input
                type="text"
                value={form.hero.title}
                onChange={e => updateHero('title', e.target.value)}
                className="w-full border border-background-300 rounded-md px-4 py-2.5 text-sm focus:outline-none focus:border-primary-400 bg-white font-heading font-bold"
              />
            </div>
            <div>
              <label className="block text-xs font-bold text-foreground-600 uppercase tracking-wide mb-1.5">Subtítulo</label>
              <textarea
                value={form.hero.subtitle}
                onChange={e => updateHero('subtitle', e.target.value)}
                rows={3}
                maxLength={200}
                className="w-full border border-background-300 rounded-md px-4 py-2.5 text-sm focus:outline-none focus:border-primary-400 bg-white resize-none"
              />
            </div>
          </div>
          <div className="space-y-3">
            <label className="block text-xs font-bold text-foreground-600 uppercase tracking-wide">Imagem de Fundo</label>
            {form.hero.image && (
              <div className="w-full h-36 rounded-lg overflow-hidden border border-background-200">
                <img src={form.hero.image} alt="hero preview" className="w-full h-full object-cover object-top" />
              </div>
            )}
            <input
              type="text"
              value={form.hero.image}
              onChange={e => updateHero('image', e.target.value)}
              placeholder="URL da imagem..."
              className="w-full border border-background-300 rounded-md px-4 py-2.5 text-sm focus:outline-none focus:border-primary-400 bg-white"
            />
            <button onClick={() => heroFileRef.current?.click()} className="w-full border-2 border-dashed border-background-300 rounded-lg py-4 text-xs text-foreground-400 hover:border-primary-300 hover:text-primary-500 transition-colors cursor-pointer flex items-center justify-center gap-2">
              <i className="ri-upload-cloud-2-line text-lg"></i> Upload de imagem
            </button>
            <input ref={heroFileRef} type="file" accept="image/*" onChange={e => handleImageFile('hero', e)} className="hidden" />
          </div>
        </div>
      )}

      {/* ── STORY TAB ── */}
      {activeTab === 'story' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="space-y-4">
            <div>
              <label className="block text-xs font-bold text-foreground-600 uppercase tracking-wide mb-1.5">Título da Seção</label>
              <input
                type="text"
                value={form.story.sectionTitle}
                onChange={e => updateStory('sectionTitle', e.target.value)}
                className="w-full border border-background-300 rounded-md px-4 py-2.5 text-sm focus:outline-none focus:border-primary-400 bg-white font-heading"
              />
            </div>
            <div>
              <label className="block text-xs font-bold text-foreground-600 uppercase tracking-wide mb-2">Parágrafos</label>
              <div className="space-y-3">
                {form.story.paragraphs.map((p, i) => (
                  <div key={i} className="relative">
                    <textarea
                      value={p}
                      onChange={e => updateParagraph(i, e.target.value)}
                      rows={3}
                      maxLength={500}
                      placeholder={`Parágrafo ${i + 1}...`}
                      className="w-full border border-background-300 rounded-md px-4 py-2.5 pr-9 text-sm focus:outline-none focus:border-primary-400 bg-white resize-none"
                    />
                    {form.story.paragraphs.length > 1 && (
                      <button onClick={() => removeParagraph(i)} className="absolute top-2 right-2 w-6 h-6 flex items-center justify-center text-foreground-400 hover:text-red-500 cursor-pointer">
                        <i className="ri-close-line text-sm"></i>
                      </button>
                    )}
                  </div>
                ))}
                <button onClick={addParagraph} className="flex items-center gap-1.5 text-xs text-primary-500 hover:text-primary-700 cursor-pointer font-bold">
                  <i className="ri-add-line"></i> Adicionar parágrafo
                </button>
              </div>
            </div>
          </div>
          <div className="space-y-3">
            <label className="block text-xs font-bold text-foreground-600 uppercase tracking-wide">Imagem da Seção</label>
            {form.story.image && (
              <div className="w-full h-48 rounded-lg overflow-hidden border border-background-200">
                <img src={form.story.image} alt="story" className="w-full h-full object-cover object-top" />
              </div>
            )}
            <input type="text" value={form.story.image} onChange={e => updateStory('image', e.target.value)} placeholder="URL da imagem..." className="w-full border border-background-300 rounded-md px-4 py-2.5 text-sm focus:outline-none focus:border-primary-400 bg-white" />
            <button onClick={() => storyFileRef.current?.click()} className="w-full border-2 border-dashed border-background-300 rounded-lg py-4 text-xs text-foreground-400 hover:border-primary-300 hover:text-primary-500 transition-colors cursor-pointer flex items-center justify-center gap-2">
              <i className="ri-upload-cloud-2-line text-lg"></i> Upload de imagem
            </button>
            <input ref={storyFileRef} type="file" accept="image/*" onChange={e => handleImageFile('story', e)} className="hidden" />
          </div>
        </div>
      )}

      {/* ── MISSION TAB ── */}
      {activeTab === 'mission' && (
        <div className="max-w-2xl space-y-4">
          <div>
            <label className="block text-xs font-bold text-foreground-600 uppercase tracking-wide mb-1.5">Título da Seção</label>
            <input type="text" value={form.mission.sectionTitle} onChange={e => updateMission('sectionTitle', e.target.value)} className="w-full border border-background-300 rounded-md px-4 py-2.5 text-sm focus:outline-none focus:border-primary-400 bg-white" />
          </div>
          <div>
            <label className="block text-xs font-bold text-foreground-600 uppercase tracking-wide mb-1.5">Frase Principal <span className="font-normal text-foreground-400 normal-case">(aparece em destaque entre aspas)</span></label>
            <textarea value={form.mission.mainText} onChange={e => updateMission('mainText', e.target.value)} rows={2} maxLength={120} className="w-full border border-background-300 rounded-md px-4 py-2.5 text-sm focus:outline-none focus:border-primary-400 bg-white font-heading font-bold resize-none" />
            <p className="text-xs text-foreground-400 text-right">{form.mission.mainText.length}/120</p>
          </div>
          <div>
            <label className="block text-xs font-bold text-foreground-600 uppercase tracking-wide mb-1.5">Descrição</label>
            <textarea value={form.mission.description} onChange={e => updateMission('description', e.target.value)} rows={4} maxLength={400} className="w-full border border-background-300 rounded-md px-4 py-2.5 text-sm focus:outline-none focus:border-primary-400 bg-white resize-none" />
          </div>
          <div className="bg-background-100 rounded-xl p-5 border border-background-200">
            <p className="text-xs text-foreground-400 mb-3 font-bold uppercase tracking-wide">Pré-visualização</p>
            <p className="text-primary-500 text-xs font-bold uppercase tracking-widest mb-2">{form.mission.sectionTitle}</p>
            <blockquote className="font-heading font-black text-foreground-950 text-xl leading-tight mb-2">&ldquo;{form.mission.mainText}&rdquo;</blockquote>
            <p className="text-foreground-500 text-xs">{form.mission.description}</p>
          </div>
        </div>
      )}

      {/* ── VALUES TAB ── */}
      {activeTab === 'values' && (
        <div className="space-y-5">
          <div className="max-w-sm">
            <label className="block text-xs font-bold text-foreground-600 uppercase tracking-wide mb-1.5">Título da Seção</label>
            <input
              type="text"
              value={form.values.sectionTitle}
              onChange={e => setForm(p => ({ ...p, values: { ...p.values, sectionTitle: e.target.value } }))}
              className="w-full border border-background-300 rounded-md px-4 py-2.5 text-sm focus:outline-none focus:border-primary-400 bg-white"
            />
          </div>
          <div className="space-y-4">
            {form.values.items.map((item, i) => (
              <div key={i} className="border border-background-200 rounded-xl p-4 bg-background-50 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-bold text-foreground-500 uppercase tracking-wide">Valor {i + 1}</span>
                  {form.values.items.length > 1 && (
                    <button onClick={() => removeValueItem(i)} className="text-foreground-400 hover:text-red-500 cursor-pointer w-6 h-6 flex items-center justify-center">
                      <i className="ri-delete-bin-line text-sm"></i>
                    </button>
                  )}
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div>
                    <label className="block text-xs text-foreground-500 mb-1">Ícone</label>
                    <select
                      value={item.icon}
                      onChange={e => updateValueItem(i, 'icon', e.target.value)}
                      className="w-full border border-background-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-primary-400 bg-white cursor-pointer"
                    >
                      {ICON_OPTIONS.map(icon => (
                        <option key={icon} value={icon}>{icon.replace('ri-', '').replace(/-line|-fill/, '')}</option>
                      ))}
                    </select>
                    <div className="mt-1 flex items-center gap-1">
                      <div className="w-7 h-7 flex items-center justify-center bg-primary-100 rounded-lg">
                        <i className={`${item.icon} text-primary-500 text-base`}></i>
                      </div>
                      <span className="text-xs text-foreground-400">pré-visualização</span>
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs text-foreground-500 mb-1">Título</label>
                    <input type="text" value={item.title} onChange={e => updateValueItem(i, 'title', e.target.value)} className="w-full border border-background-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-primary-400 bg-white" />
                  </div>
                  <div>
                    <label className="block text-xs text-foreground-500 mb-1">Descrição</label>
                    <textarea value={item.description} onChange={e => updateValueItem(i, 'description', e.target.value)} rows={3} maxLength={200} className="w-full border border-background-300 rounded-md px-3 py-2 text-xs focus:outline-none focus:border-primary-400 bg-white resize-none" />
                  </div>
                </div>
              </div>
            ))}
            {form.values.items.length < 6 && (
              <button onClick={addValueItem} className="flex items-center gap-2 text-sm text-primary-500 hover:text-primary-700 cursor-pointer font-bold border-2 border-dashed border-primary-200 rounded-xl px-4 py-3 w-full justify-center hover:border-primary-400 transition-colors">
                <i className="ri-add-line"></i> Adicionar valor
              </button>
            )}
          </div>
        </div>
      )}

      {/* ── STATS TAB ── */}
      {activeTab === 'stats' && (
        <div className="space-y-4">
          <p className="text-foreground-500 text-sm">Esses números aparecem na barra laranja logo abaixo do hero.</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {form.stats.items.map((stat, i) => (
              <div key={i} className="border border-background-200 rounded-xl p-4 bg-background-50 space-y-2">
                <p className="text-xs font-bold text-foreground-500 uppercase tracking-wide">Número {i + 1}</p>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs text-foreground-500 mb-1">Valor</label>
                    <input type="text" value={stat.value} onChange={e => updateStatItem(i, 'value', e.target.value)} placeholder="Ex: 50K+" className="w-full border border-background-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-primary-400 bg-white font-bold" />
                  </div>
                  <div>
                    <label className="block text-xs text-foreground-500 mb-1">Label</label>
                    <input type="text" value={stat.label} onChange={e => updateStatItem(i, 'label', e.target.value)} placeholder="Ex: Tutores mensais" className="w-full border border-background-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-primary-400 bg-white" />
                  </div>
                </div>
                <div className="bg-primary-500 rounded-lg px-3 py-2 text-center">
                  <p className="font-heading font-black text-white text-lg leading-none">{stat.value || '—'}</p>
                  <p className="text-primary-100 text-xs">{stat.label || '—'}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── CTA TAB ── */}
      {activeTab === 'cta' && (
        <div className="max-w-2xl space-y-4">
          <div>
            <label className="block text-xs font-bold text-foreground-600 uppercase tracking-wide mb-1.5">Título do CTA</label>
            <input type="text" value={form.cta.title} onChange={e => updateCta('title', e.target.value)} className="w-full border border-background-300 rounded-md px-4 py-2.5 text-sm focus:outline-none focus:border-primary-400 bg-white font-heading font-bold" />
          </div>
          <div>
            <label className="block text-xs font-bold text-foreground-600 uppercase tracking-wide mb-1.5">Subtítulo</label>
            <textarea value={form.cta.subtitle} onChange={e => updateCta('subtitle', e.target.value)} rows={3} maxLength={300} className="w-full border border-background-300 rounded-md px-4 py-2.5 text-sm focus:outline-none focus:border-primary-400 bg-white resize-none" />
          </div>
          <div className="bg-primary-500 rounded-xl p-6 text-center">
            <p className="font-heading font-black text-white text-xl mb-2">{form.cta.title || '—'}</p>
            <p className="text-primary-100 text-sm">{form.cta.subtitle || '—'}</p>
          </div>
        </div>
      )}
    </div>
  );
}